'use client'

import { useLanguage, useTexts } from '@/contexts/LanguageContext'
import { useTrustCenterConfig } from '@/contexts/TrustCenterContext'
import { cn } from '@/lib/utils'
import { useCertifications } from '../certifications/useCertifications'
import { CONTROL_COUNT, CONTROL_DOMAINS } from '../controls/controlDomains'

function useStats() {
  const { STATS } = useTexts().PUBLIC_TRUST_CENTER.OVERVIEW
  const { resources, subprocessors, showControls } = useTrustCenterConfig()
  const certifications = useCertifications()
  const stats = [
    { label: STATS.CERTIFICATIONS, value: certifications.length, isVisible: true },
    { label: STATS.CONTROLS, value: CONTROL_COUNT, isVisible: showControls },
    { label: STATS.DOCUMENTS, value: resources.public.length + resources.private.length, isVisible: true },
    { label: STATS.SUBPROCESSORS, value: subprocessors.length, isVisible: true },
  ]
  return stats.filter((stat) => stat.isVisible)
}

// @TODO: Use the real last update date once the API exposes it
function useStatusLine() {
  const { OVERVIEW } = useTexts().PUBLIC_TRUST_CENTER
  const lang = useLanguage()
  const { showControls } = useTrustCenterConfig()
  const today = new Intl.DateTimeFormat(lang, { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date())
  const lastUpdated = OVERVIEW.LAST_UPDATED(today)
  return showControls ? `${lastUpdated} · ${OVERVIEW.MONITORING(CONTROL_COUNT, CONTROL_DOMAINS.length)}` : lastUpdated
}

export default function HeroBanner() {
  const { PUBLIC_TRUST_CENTER } = useTexts()
  const { organizationName, tagline } = useTrustCenterConfig()
  const stats = useStats()
  const statusLine = useStatusLine()
  return (
    <section className="bg-trust-center bg-[url(/trust-center/hero-background.png)] bg-cover bg-center">
      <div className="mx-auto flex w-full max-w-screen-2xl flex-col gap-10 px-4 py-11 md:px-12 lg:min-h-78 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex max-w-2xl flex-col items-start gap-2">
          <h1 className="text-4xl font-bold tracking-tight text-pretty text-white">
            {organizationName ? PUBLIC_TRUST_CENTER.PAGE_TITLE(organizationName) : PUBLIC_TRUST_CENTER.TITLE}
          </h1>
          {tagline && <p className="text-sm/relaxed text-pretty text-white/75">{tagline}</p>}
        </div>
        <div className="flex shrink-0 flex-col gap-3">
          <div
            className={cn(
              'grid gap-px overflow-hidden rounded-md border border-white/15 bg-white/15',
              stats.length === 4 ? 'grid-cols-2' : 'grid-cols-3',
            )}
          >
            {stats.map((stat) => (
              <div key={stat.label} className="flex min-w-48 flex-col gap-1.5 bg-trust-center-deep px-6 py-5">
                <span className="font-mono text-3xl font-bold text-white">{stat.value}</span>
                <span className="text-xs text-white/70">{stat.label}</span>
              </div>
            ))}
          </div>
          <span className="text-xs text-white/70">{statusLine}</span>
        </div>
      </div>
    </section>
  )
}
